<script setup lang="ts">
import type { SsrRequestRecord } from '~/composables/useServerFetch'
import { PERF_DEBUG } from '~/utils/constants'

const SSR_REQUEST_INSPECTOR_READY_EVENT = 'ssr-request-inspector-ready'

// ===== Composables =====
const props = withDefaults(
  defineProps<{
    triggerPosition?: 'left' | 'right'
    clearOnRouteChange?: boolean
  }>(),
  {
    triggerPosition: 'right',
    clearOnRouteChange: true,
  },
)
const route = useRoute()
/** SSR 请求调试记录，由 useServerFetch 在 SSR 阶段写入 useState */
const records = useState<SsrRequestRecord[]>('ssr-request-inspector', () => [])

/** 页面缓存状态，由 cache-vary-country 中间件通过 window.__CACHE_STATUS__ 注入 */
const pageCacheStatus = ref('unknown')
/** 当前请求的服务器时间戳，由 cache-vary-country 中间件通过 window.__SSR_REQUEST_TIME__ 注入 */
const serverRequestTime = ref<number | null>(null)

onMounted(() => {
  window.addEventListener(SSR_REQUEST_INSPECTOR_READY_EVENT, syncInjectedState)
  syncInjectedState()
})

// ===== Computed =====
/** 错误边界下路由查询可能为空，需从浏览器地址恢复失败请求的 ?p=1。 */
const isVisible = computed(
  () =>
    PERF_DEBUG &&
    (route.query.p === '1' || (import.meta.client && new URLSearchParams(window.location.search).get('p') === '1')),
)
const triggerPositionClass = computed(() =>
  props.triggerPosition === 'left'
    ? 'left-[20px] rtl:right-[20px] rtl:left-auto'
    : 'right-[20px] rtl:left-[20px] rtl:right-auto',
)
const successCount = computed(() => records.value.filter((r) => r.status === 'success').length)
const errorCount = computed(() => records.value.filter((r) => r.status === 'error').length)
/** 按搜索关键词过滤请求列表（支持 URL、响应体、参数、请求头、错误信息全字段搜索） */
const filteredRecords = computed(() => {
  const query = searchQuery.value.trim().toLowerCase()
  if (!query) return records.value
  return records.value.filter((r) => {
    const haystack = [
      r.url,
      r.responseBody || '',
      r.error || '',
      r.params ? JSON.stringify(r.params) : '',
      r.headers ? JSON.stringify(r.headers) : '',
    ]
      .join(' ')
      .toLowerCase()
    return haystack.includes(query)
  })
})

// ===== Refs =====
/** 调试面板展开状态 */
const isPanelOpen = ref(false)
/** 当前展开详情的记录索引，null 表示全部收起 */
const expandedIndex = ref<number | null>(null)
/** 搜索关键词 */
const searchQuery = ref('')
/** 当前已复制的字段标识，用于显示 "Copied!" 反馈 */
const copiedField = ref<string | null>(null)
/** 面板宽度 (px)，默认 560 */
const panelWidth = ref(560)
/** 是否正在拖拽调整宽度 */
const isResizing = ref(false)

// ===== Watch =====
/** 客户端路由切换时清空旧记录，避免显示上一页的 SSR 请求 */
watch(
  () => route.path,
  () => {
    if (!props.clearOnRouteChange) return
    records.value = []
    expandedIndex.value = null
    isPanelOpen.value = false
    searchQuery.value = ''
    pageCacheStatus.value = 'unknown'
    serverRequestTime.value = null
  },
)

// ===== Functions =====
/** 同步服务端写入页面尾部的调试记录与缓存元数据。 */
const syncInjectedState = () => {
  const injectedRecords = (window as any).__SSR_REQUEST_RECORDS__
  if (Array.isArray(injectedRecords)) {
    records.value = injectedRecords
  }
  const injected = (window as any).__CACHE_STATUS__
  if (typeof injected === 'string' && injected) {
    pageCacheStatus.value = injected
  }
  const reqTime = (window as any).__SSR_REQUEST_TIME__
  if (typeof reqTime === 'number' && reqTime > 0) {
    serverRequestTime.value = reqTime
  }
}

/** 切换调试面板展开/收起 */
const togglePanel = () => {
  isPanelOpen.value = !isPanelOpen.value
  if (isPanelOpen.value) {
    panelWidth.value = 560
  }
}

// ===== 拖拽调整宽度 =====
const MIN_PANEL_WIDTH = 560
const RESIZE_MARGIN = 48

let resizeStartX = 0
let resizeStartWidth = 0

const onResizePointerDown = (e: PointerEvent) => {
  e.preventDefault()
  isResizing.value = true
  resizeStartX = e.clientX
  resizeStartWidth = panelWidth.value
  document.addEventListener('pointermove', onResizeMove)
  document.addEventListener('pointerup', onResizeEnd)
  document.body.style.userSelect = 'none'
  document.body.style.cursor = 'col-resize'
}

const onResizeMove = (e: PointerEvent) => {
  if (!isResizing.value) return
  const delta = resizeStartX - e.clientX
  const newWidth = resizeStartWidth + delta
  const maxWidth = window.innerWidth - RESIZE_MARGIN
  panelWidth.value = Math.min(Math.max(newWidth, MIN_PANEL_WIDTH), maxWidth)
}

const onResizeEnd = () => {
  isResizing.value = false
  document.removeEventListener('pointermove', onResizeMove)
  document.removeEventListener('pointerup', onResizeEnd)
  document.body.style.userSelect = ''
  document.body.style.cursor = ''
}

onUnmounted(() => {
  window.removeEventListener(SSR_REQUEST_INSPECTOR_READY_EVENT, syncInjectedState)
  document.removeEventListener('pointermove', onResizeMove)
  document.removeEventListener('pointerup', onResizeEnd)
  document.body.style.userSelect = ''
  document.body.style.cursor = ''
})

/** 切换某条记录的详情展开 */
const toggleDetail = (index: number) => {
  expandedIndex.value = expandedIndex.value === index ? null : index
}

/** 将时间戳格式化为可读时间 */
const formatTime = (timestamp: number): string => {
  return new Date(timestamp).toLocaleTimeString()
}

/**
 * 安全拼接 baseURL 和 path，确保中间恰好一个 /
 * 修复 WIKIBITWEBV1 无前导 / 导致的 URL 缺失斜杠问题
 */
const joinUrl = (base?: string, path?: string): string => {
  if (!base) return path || ''
  if (!path) return base
  if (base.endsWith('/') && path.startsWith('/')) return `${base}${path.slice(1)}`
  if (!base.endsWith('/') && !path.startsWith('/')) return `${base}/${path}`
  return `${base}${path}`
}

/** 获取完整请求 URL */
const getFullUrl = (record: SsrRequestRecord): string => joinUrl(record.baseURL, record.url)

/**
 * 获取 HTTP 方法对应的 Swagger 风格样式
 * GET=蓝 POST=绿 PUT=橙 DELETE=红 PATCH=青
 */
const getMethodStyle = (method: string): { badge: string; border: string } => {
  const m = method.toUpperCase()
  const map: Record<string, { badge: string; border: string }> = {
    GET: { badge: 'bg-[#61affe] text-white', border: 'border-l-[4px] border-l-[#61affe]' },
    POST: { badge: 'bg-[#49cc90] text-white', border: 'border-l-[4px] border-l-[#49cc90]' },
    PUT: { badge: 'bg-[#fca130] text-white', border: 'border-l-[4px] border-l-[#fca130]' },
    DELETE: { badge: 'bg-[#f93e3e] text-white', border: 'border-l-[4px] border-l-[#f93e3e]' },
    PATCH: { badge: 'bg-[#50e3c2] text-white', border: 'border-l-[4px] border-l-[#50e3c2]' },
  }
  return map[m] || { badge: 'bg-[#7e57c2] text-white', border: 'border-l-[4px] border-l-[#7e57c2]' }
}

/** 获取请求状态对应的样式 */
const getStatusClass = (record: SsrRequestRecord): string => {
  return record.status === 'success' ? 'bg-[#1b3a1b] text-[#49cc90]' : 'bg-[#3a1b1b] text-[#f44336]'
}

/** 获取状态显示文本 */
const getStatusText = (record: SsrRequestRecord): string => {
  if (record.status === 'success') return String(record.statusCode || 200)
  return String(record.statusCode || 'ERR')
}

/** 尝试解析 JSON 字符串，失败返回 null */
const tryParseJson = (raw: string): unknown => {
  const t = raw.trim()
  if (!t) return null
  try {
    return JSON.parse(t)
  } catch {
    return null
  }
}

/** 格式化请求体：对象/数组则 JSON 美化，字符串原样返回 */
const formatBody = (body: unknown): string => {
  if (body === null || body === undefined) return ''
  if (typeof body === 'string') return body
  try {
    return JSON.stringify(body, null, 2)
  } catch {
    return String(body)
  }
}

/**
 * 解析请求体用于树形渲染
 * @returns { kind: 'json' | 'text' | 'none', value?: unknown | string }
 */
const renderBody = (record: SsrRequestRecord): { kind: 'json' | 'text' | 'none'; value?: unknown } => {
  const b = record.body
  if (b === undefined || b === null || b === '') return { kind: 'none' }
  if (typeof b === 'object') return { kind: 'json', value: b }
  if (typeof b === 'string') {
    const p = tryParseJson(b)
    if (p !== null && typeof p === 'object') return { kind: 'json', value: p }
    return { kind: 'text', value: b }
  }
  return { kind: 'text', value: String(b) }
}

/**
 * 解析响应体用于树形渲染
 * @returns { kind: 'json' | 'text' | 'none', value?: unknown | string }
 */
const renderResponse = (record: SsrRequestRecord): { kind: 'json' | 'text' | 'none'; value?: unknown } => {
  const r = record.responseBody
  if (!r) return { kind: 'none' }
  const p = tryParseJson(r)
  if (p !== null && typeof p === 'object') return { kind: 'json', value: p }
  return { kind: 'text', value: r }
}

/** 复制文本到剪贴板，并显示短暂反馈 */
const copyToClipboard = async (text: string, field: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(text)
  } catch {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
  }
  copiedField.value = field
  setTimeout(() => {
    if (copiedField.value === field) copiedField.value = null
  }, 2000)
}

/** 复制完整响应数据 */
const copyResponse = async (record: SsrRequestRecord, index: number): Promise<void> => {
  if (record.responseBody) {
    await copyToClipboard(record.responseBody, `response-${index}`)
  }
}

/**
 * 根据 SSR 请求记录生成可粘贴到 Postman 的 curl 命令
 * 使用 joinUrl 修复 URL 拼接问题
 */
const generateCurl = (record: SsrRequestRecord): string => {
  const fullUrl = getFullUrl(record)
  const method = record.method.toUpperCase()

  let url = fullUrl
  if (record.params && Object.keys(record.params).length) {
    const searchParams = new URLSearchParams()
    for (const [key, value] of Object.entries(record.params)) {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value))
      }
    }
    const queryString = searchParams.toString()
    if (queryString) {
      url += `?${queryString}`
    }
  }

  const parts = [`curl -X ${method} '${url}'`]

  if (record.headers) {
    for (const [key, value] of Object.entries(record.headers)) {
      const escapedValue = value.replace(/'/g, "'\\''")
      parts.push(`  -H '${key}: ${escapedValue}'`)
    }
  }

  if (record.body !== undefined && record.body !== null && ['POST', 'PUT', 'PATCH'].includes(method)) {
    const bodyStr = typeof record.body === 'string' ? record.body : JSON.stringify(record.body)
    const escapedBody = bodyStr.replace(/'/g, "'\\''")
    parts.push(`  -d '${escapedBody}'`)
  }

  return parts.join(' \\\n')
}

/** 复制 curl 命令 */
const copyCurl = async (record: SsrRequestRecord, index: number): Promise<void> => {
  await copyToClipboard(generateCurl(record), `curl-${index}`)
}

/** 缓存状态对应的标签样式 */
const getCacheStatusStyle = (status: string): string => {
  if (status === 'unknown') return 'bg-[#9e9e9e] text-white'
  if (status === 'ssr') return 'bg-[#2196f3] text-white'
  if (status.startsWith('cache-hit-fresh')) return 'bg-[#4caf50] text-white'
  if (status.startsWith('cache-hit-stale')) return 'bg-[#ffc107] text-[#1b1b2e]'
  if (status === 'client-cache-hit') return 'bg-white text-[#1b1b2e]'
  return 'bg-[#666] text-white'
}

/** 缓存状态对应的圆点颜色（浮动按钮指示器用） */
const getCacheStatusDot = (status: string): string => {
  if (status === 'unknown') return 'bg-[#9e9e9e]'
  if (status === 'ssr') return 'bg-[#2196f3]'
  if (status.startsWith('cache-hit-fresh')) return 'bg-[#4caf50]'
  if (status.startsWith('cache-hit-stale')) return 'bg-[#ffc107]'
  if (status === 'client-cache-hit') return 'bg-white'
  return 'bg-[#aaa]'
}
</script>

<template>
  <div v-if="isVisible">
    <!-- 遮罩层 -->
    <div v-if="isPanelOpen" class="fixed inset-0 z-[9998] bg-black/50" @click="togglePanel" />

    <!-- Swagger 风格调试面板 -->
    <div
      v-if="isPanelOpen"
      class="fixed inset-y-0 right-0 z-[9999] flex flex-col bg-[#fafafa] shadow-2xl rtl:left-0"
      :style="{ width: panelWidth + 'px' }"
    >
      <!-- 头部 -->
      <div class="flex items-center justify-between bg-[#1b1b2e] px-[20px] py-[14px] text-white">
        <div class="flex items-center gap-[10px]">
          <!-- 缓存状态徽章 -->
          <span
            class="rounded-[4px] px-[6px] py-[2px] text-[10px] font-bold"
            :class="getCacheStatusStyle(pageCacheStatus)"
            >{{ pageCacheStatus }}</span
          >
          <span
            v-if="serverRequestTime"
            class="rounded-[4px] bg-[#33334a] px-[6px] py-[2px] font-mono text-[10px] text-[#a0d0a0]"
            >{{ formatTime(serverRequestTime) }}</span
          >
          <span class="text-[16px] font-bold">SSR Request Inspector</span>
          <span class="rounded-[12px] bg-[#49cc90] px-[8px] py-[2px] text-[11px] font-bold text-[#1b1b2e]">
            {{ records.length }}
          </span>
        </div>
        <div
          class="flex size-[28px] cursor-pointer items-center justify-center rounded-[4px] text-[#a0a0b0] hover:bg-white/10 hover:text-white"
          @click="togglePanel"
        >
          <svg class="size-[16px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </div>
      </div>

      <!-- 左侧拖拽手柄：胶囊式设计，居中嵌于面板左边缘 -->
      <div
        class="group absolute top-1/2 left-0 z-10 flex h-[48px] w-[16px] -translate-x-1/2 -translate-y-1/2 cursor-col-resize items-center justify-center"
        @pointerdown="onResizePointerDown"
      >
        <div
          class="flex h-[40px] w-[6px] items-center justify-center rounded-full border transition-all duration-150"
          :class="
            isResizing
              ? 'border-[#49cc90]/50 bg-[#49cc90]/15 shadow-[0_0_10px_rgba(73,204,144,0.4)]'
              : 'border-white/15 bg-white/20 group-hover:border-white/30 group-hover:bg-white/35'
          "
        >
          <div class="flex flex-col items-center gap-[2px]">
            <span
              class="block h-[1px] w-[3px] rounded-full transition-colors duration-150"
              :class="isResizing ? 'bg-[#49cc90]' : 'bg-white/50 group-hover:bg-white/70'"
            />
            <span
              class="block h-[1px] w-[3px] rounded-full transition-colors duration-150"
              :class="isResizing ? 'bg-[#49cc90]' : 'bg-white/50 group-hover:bg-white/70'"
            />
            <span
              class="block h-[1px] w-[3px] rounded-full transition-colors duration-150"
              :class="isResizing ? 'bg-[#49cc90]' : 'bg-white/50 group-hover:bg-white/70'"
            />
          </div>
        </div>
      </div>

      <!-- 搜索 + 统计 -->
      <div class="flex items-center gap-[12px] border-b border-[#e0e0e0] bg-white px-[20px] py-[10px]">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search URL, response, params..."
          class="flex-1 rounded-[4px] border border-[#d0d0d0] bg-[#f5f5f5] px-[10px] py-[6px] font-mono text-[12px] text-[#333] outline-none placeholder:text-[#999] focus:border-[#49cc90] focus:bg-white"
        />
        <div class="flex shrink-0 gap-[12px] text-[11px]">
          <span class="font-semibold text-[#49cc90]">{{ successCount }} ok</span>
          <span class="font-semibold text-[#f93e3e]">{{ errorCount }} err</span>
        </div>
      </div>

      <!-- 请求列表（Swagger 卡片风格） -->
      <div class="flex-1 overflow-y-auto bg-[#fafafa] px-[16px] py-[12px]">
        <div v-for="(record, index) in filteredRecords" :key="index" class="mb-[8px]">
          <!-- 请求卡片头部 -->
          <div
            class="flex cursor-pointer items-center gap-[10px] rounded-[4px] bg-white px-[12px] py-[10px] shadow-sm hover:shadow-md"
            :class="getMethodStyle(record.method).border"
            @click="toggleDetail(index)"
          >
            <!-- 方法标签 -->
            <span
              class="shrink-0 rounded-[3px] px-[6px] py-[2px] text-[10px] font-bold"
              :class="getMethodStyle(record.method).badge"
            >
              {{ record.method.toUpperCase() }}
            </span>
            <!-- URL 路径 -->
            <span class="flex-1 truncate font-mono text-[12px] text-[#333]">
              {{ getFullUrl(record) }}
            </span>
            <!-- 状态码 -->
            <span
              class="shrink-0 rounded-[3px] px-[5px] py-[1px] text-[10px] font-semibold"
              :class="getStatusClass(record)"
            >
              {{ getStatusText(record) }}
            </span>
            <!-- 耗时 -->
            <span class="shrink-0 text-[10px] text-[#999]">{{ record.duration }}ms</span>
            <!-- 展开/收起箭头 -->
            <svg
              class="size-[12px] shrink-0 text-[#999] transition-transform"
              :class="{ 'rotate-180': expandedIndex === index }"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>

          <!-- 详情展开区域 -->
          <div
            v-if="expandedIndex === index"
            class="mt-[1px] rounded-b-[4px] border border-t-0 border-[#e0e0e0] bg-white px-[16px] py-[14px]"
            :class="getMethodStyle(record.method).border"
          >
            <!-- 基本信息 -->
            <div class="mb-[12px] grid gap-[6px] text-[12px]">
              <div class="flex gap-[8px]">
                <span class="w-[70px] shrink-0 font-semibold text-[#666]">URL</span>
                <span class="font-mono break-all text-[#333]">{{ getFullUrl(record) }}</span>
              </div>
              <div class="flex gap-[8px]">
                <span class="w-[70px] shrink-0 font-semibold text-[#666]">Method</span>
                <span class="font-mono text-[#333]">{{ record.method }}</span>
              </div>
              <div class="flex gap-[8px]">
                <span class="w-[70px] shrink-0 font-semibold text-[#666]">Fetch Time</span>
                <span class="font-mono text-[#333]">{{ formatTime(record.timestamp) }}</span>
              </div>
              <div v-if="serverRequestTime" class="flex gap-[8px]">
                <span class="w-[70px] shrink-0 font-semibold text-[#666]">Req Time</span>
                <span class="font-mono text-[#49cc90]">{{ formatTime(serverRequestTime) }}</span>
              </div>
              <div class="flex gap-[8px]">
                <span class="w-[70px] shrink-0 font-semibold text-[#666]">Duration</span>
                <span class="font-mono text-[#333]">{{ record.duration }}ms</span>
              </div>
            </div>

            <!-- Parameters -->
            <div v-if="record.params && Object.keys(record.params).length" class="mb-[12px]">
              <div
                class="mb-[6px] flex items-center gap-[6px] border-b border-[#eee] pb-[4px] text-[12px] font-bold text-[#666]"
              >
                <svg class="size-[12px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                </svg>
                Parameters
              </div>
              <pre
                class="max-h-[150px] overflow-auto rounded-[4px] bg-[#f8f8f8] p-[10px] font-mono text-[11px] break-all whitespace-pre-wrap text-[#555]"
                >{{ JSON.stringify(record.params, null, 2) }}</pre
              >
            </div>

            <!-- Headers -->
            <div v-if="record.headers && Object.keys(record.headers).length" class="mb-[12px]">
              <div
                class="mb-[6px] flex items-center gap-[6px] border-b border-[#eee] pb-[4px] text-[12px] font-bold text-[#666]"
              >
                <svg class="size-[12px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                Headers
              </div>
              <div
                class="max-h-[150px] overflow-auto rounded-[4px] bg-[#1e1e2e] p-[10px] font-mono text-[11px] text-[#a0d0a0]"
              >
                <DevJsonNode :data="record.headers" theme="dark" :default-open="1" />
              </div>
            </div>

            <!-- Request Body -->
            <div v-if="record.body !== undefined && record.body !== null && record.body !== ''" class="mb-[12px]">
              <div class="mb-[6px] flex items-center justify-between border-b border-[#eee] pb-[4px]">
                <span class="flex items-center gap-[6px] text-[12px] font-bold text-[#666]">
                  <svg class="size-[12px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M4 7V4h16v3M9 20h6M12 4v16" />
                  </svg>
                  Request Body
                </span>
                <div class="flex items-center gap-[6px]">
                  <span v-if="copiedField === `body-${index}`" class="text-[11px] font-semibold text-[#49cc90]"
                    >Copied!</span
                  >
                  <div
                    v-else
                    class="flex cursor-pointer items-center gap-[4px] rounded-[3px] border border-[#d0d0d0] px-[6px] py-[2px] text-[11px] text-[#666] hover:bg-[#f0f0f0]"
                    @click="copyToClipboard(formatBody(record.body), `body-${index}`)"
                  >
                    <svg class="size-[10px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                    Copy
                  </div>
                </div>
              </div>
              <div
                class="max-h-[220px] overflow-auto rounded-[4px] bg-[#1e1e2e] p-[10px] font-mono text-[11px] text-[#a0d0a0]"
              >
                <DevJsonNode
                  v-if="renderBody(record).kind === 'json'"
                  :data="renderBody(record).value"
                  theme="dark"
                  :default-open="2"
                />
                <pre
                  v-else-if="renderBody(record).kind === 'text'"
                  class="break-all whitespace-pre-wrap text-[#a0d0a0]"
                  >{{ renderBody(record).value }}</pre
                >
              </div>
            </div>

            <!-- Response -->
            <div v-if="record.responseBody" class="mb-[12px]">
              <div class="mb-[6px] flex items-center justify-between border-b border-[#eee] pb-[4px]">
                <span class="flex items-center gap-[6px] text-[12px] font-bold text-[#666]">
                  <svg class="size-[12px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  Response
                </span>
                <div class="flex items-center gap-[6px]">
                  <span v-if="copiedField === `response-${index}`" class="text-[11px] font-semibold text-[#49cc90]"
                    >Copied!</span
                  >
                  <div
                    v-else
                    class="flex cursor-pointer items-center gap-[4px] rounded-[3px] border border-[#d0d0d0] px-[6px] py-[2px] text-[11px] text-[#666] hover:bg-[#f0f0f0]"
                    @click="copyResponse(record, index)"
                  >
                    <svg class="size-[10px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                    Copy
                  </div>
                </div>
              </div>
              <div
                class="max-h-[220px] overflow-auto rounded-[4px] bg-[#1e1e2e] p-[10px] font-mono text-[11px] text-[#a0d0a0]"
              >
                <DevJsonNode
                  v-if="renderResponse(record).kind === 'json'"
                  :data="renderResponse(record).value"
                  theme="dark"
                  :default-open="2"
                />
                <pre v-else-if="renderResponse(record).kind === 'text'" class="break-all whitespace-pre-wrap">{{
                  record.responseBody
                }}</pre>
              </div>
            </div>

            <!-- Error -->
            <div v-if="record.error" class="mb-[12px]">
              <div class="mb-[6px] border-b border-[#eee] pb-[4px] text-[12px] font-bold text-[#f93e3e]">Error</div>
              <pre
                class="rounded-[4px] bg-[#fff0f0] p-[10px] font-mono text-[11px] break-all whitespace-pre-wrap text-[#f93e3e]"
                >{{ record.error }}</pre
              >
            </div>

            <!-- cURL 命令 -->
            <div class="border-t border-[#eee] pt-[10px]">
              <div class="mb-[6px] flex items-center justify-between">
                <span class="text-[12px] font-bold text-[#666]">cURL Command</span>
                <div class="flex items-center gap-[6px]">
                  <span v-if="copiedField === `curl-${index}`" class="text-[11px] font-semibold text-[#49cc90]"
                    >Copied!</span
                  >
                  <div
                    v-else
                    class="flex cursor-pointer items-center gap-[4px] rounded-[3px] bg-[#1b1b2e] px-[8px] py-[3px] text-[11px] font-medium text-white hover:bg-[#2a2a4a]"
                    @click="copyCurl(record, index)"
                  >
                    <svg class="size-[10px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                    Copy cURL
                  </div>
                </div>
              </div>
              <pre
                class="max-h-[150px] overflow-auto rounded-[4px] bg-[#1e1e2e] p-[10px] font-mono text-[11px] break-all whitespace-pre-wrap text-[#7ec0ee]"
                >{{ generateCurl(record) }}</pre
              >
            </div>
          </div>
        </div>

        <!-- 空状态 -->
        <div
          v-if="filteredRecords.length === 0"
          class="flex items-center justify-center py-[60px] text-[14px] text-[#999]"
        >
          {{ searchQuery ? 'No matching requests' : 'No SSR requests recorded' }}
        </div>
      </div>
    </div>

    <!-- 浮动标识符 -->
    <div
      v-if="!isPanelOpen"
      class="fixed bottom-[20px] z-[9999] flex cursor-pointer items-center gap-[6px] rounded-[8px] bg-[#1b1b2e] px-[14px] py-[10px] text-white shadow-lg hover:bg-[#2a2a4a]"
      :class="triggerPositionClass"
      @click="togglePanel"
    >
      <!-- 缓存状态圆点指示器 -->
      <span
        class="size-[8px] shrink-0 rounded-full"
        :class="getCacheStatusDot(pageCacheStatus)"
        :title="`Page: ${pageCacheStatus} | Server: ${serverRequestTime ? formatTime(serverRequestTime) : 'N/A'}`"
      />
      <span class="text-[13px] font-semibold tracking-wide">SSR</span>
      <span
        class="flex size-[20px] items-center justify-center rounded-full bg-[#49cc90] text-[11px] font-bold text-[#1b1b2e]"
      >
        {{ records.length }}
      </span>
    </div>
  </div>
</template>
